import React, {useState} from "react";
import {Dialog, Grid, IconButton, Tooltip} from "@mui/material";
import {Add, Search} from "@mui/icons-material";
import SearchDialog from "../search-dialog/search-dialog";
import {PageResponse} from "../util/page-response";
import {CustomDialog} from "../custom-dialog/custom-dialog";
import CustomTextField from "../custom-text-field/custom-text-field";
import {QrCodeScanner} from "../../qr-code-scanner/qr-code-scanner";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import {BarCodeScanner} from "../../qr-code-scanner/code128-scanner";

interface ForeignKeyProps {
    initialSort?: string;
    label: string;
    value: any;
    fieldKey: string;
    fieldDescription: string;
    onAdd?: (item?: any) => void;
    onSelect?: (item: any, index: number) => void;
    onChange?: (item: any, index?: number) => void;
    findAll: (number?: number, size?: number, sort?: string, filter?: string) => Promise<PageResponse<any>>;
    dataColumnNames: string[];
    dataColumns: string[];
    dataTypes: string[];
    addComponent?: any;
    disabled?: boolean;
    fullWidth?: boolean;
    autoFocus?: boolean;
    required?: boolean;
    labelDescription: string;
    showQrCodeButton?: boolean;
    showBarCodeButton?: boolean;
}

const ForeignKey = React.memo(({
                                   value,
                                   fieldKey,
                                   fieldDescription,
                                   onAdd,
                                   onSelect,
                                   onChange,
                                   findAll,
                                   dataColumnNames,
                                   dataColumns,
                                   dataTypes,
                                   label,
                                   initialSort,
                                   addComponent,
                                   disabled,
                                   fullWidth,
                                   autoFocus,
                                   required,
                                   labelDescription,
                                   showQrCodeButton,
                                   showBarCodeButton
                               }: ForeignKeyProps) => {

    const [showSearchDialog, setShowSearchDialog] = useState(false);

    const [showAddDialog, setShowAddDialog] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);
    const [barCodeOpen, setBarCodeOpen] = useState(false);

    const getValueKey = () => {
        if (value) {
            return value[fieldKey] || '';
        } else {
            return ''
        }
    }

    const getValueDescription = () => {
        if (value) {
            return value[fieldDescription] || '';
        } else {
            return '';
        }
    }

    const openSearchDialog = () => {
        setShowSearchDialog(true);
    }

    const closeSearchDialog = () => {
        setShowSearchDialog(false);
    }

    const openAddDialog = () => {
        setShowAddDialog(true);
    }

    const closeAddDialog = (item?: any) => {
        setShowAddDialog(false);
        if (onAdd) {
            onAdd(item);
        }
    }

    const selectDataFromSearchDialog = (item: any, index: number) => {
        closeSearchDialog();
        if (onSelect) {
            onSelect(item, index);
        }
    }

    const renderSearchDialog = () => {
        return showSearchDialog && (
            <SearchDialog open={showSearchDialog} onClose={closeSearchDialog} findAll={findAll}
                          dataColumnNames={dataColumnNames} dataColumns={dataColumns}
                          dataTypes={dataTypes} label={label}
                          onSelectData={selectDataFromSearchDialog} initialSort={initialSort}/>
        )
    }

    const renderAddDialog = () => {
        return showAddDialog && (
            <CustomDialog open={showAddDialog} onClose={closeAddDialog}
                          title={label + ' - Inserir'}>{addComponent}</CustomDialog>
        )
    }

    const onChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(event.target.value);
        }
    }

    const renderQrCodeButton = () => {
        return showQrCodeButton && (
            <Tooltip title='Ler QR Code'>
                <IconButton onClick={() => setQrOpen(true)}>
                    <QrCodeScannerIcon/>
                </IconButton>
            </Tooltip>
        )
    }

    const renderBarCodeButton = () => {
        return showBarCodeButton && (
            <Tooltip title='Ler Código de Barras'>
                <IconButton onClick={() => setBarCodeOpen(true)}>
                    <DocumentScannerIcon/>
                </IconButton>
            </Tooltip>
        )
    }

    function renderAddButton() {
        if (!onAdd) {
            return null;
        }
        if (disabled) {
            return (
                <IconButton color={"primary"} disabled onClick={() => {
                    openAddDialog()
                }}><Add/></IconButton>
            );
        }
        return (
            <Tooltip title='Inserir'>
                <IconButton color={"primary"} onClick={() => {
                    openAddDialog()
                }}><Add/></IconButton>
            </Tooltip>
        );
    }

    const renderSearchButton = () => {
        if (disabled) {
            return (
                <IconButton color={"primary"} disabled onClick={() => {
                    openSearchDialog()
                }}><Search/></IconButton>
            )
        } else {
            return (
                <React.Fragment>
                    <Tooltip title='Pesquisar'>
                        <IconButton color={"primary"} onClick={() => {
                            openSearchDialog()
                        }}><Search/></IconButton>
                    </Tooltip>
                    {renderQrCodeButton()}
                    {renderBarCodeButton()}
                </React.Fragment>
            )
        }
    }

    const handleQrCodeScan = (text: string) => {
        const data = JSON.parse(text);
        const idSelected = data.id;
        setQrOpen(false);
        if (onChange) {
            onChange(idSelected);
        }
    };

    const handleBarCodeScan = (text: string) => {
        const idSelected = Number(text);
        setBarCodeOpen(false);
        if (onChange) {
            onChange(idSelected);
        }
    };

    const renderQrCodeDialog = () => {
        return (
            <Dialog open={qrOpen} onClose={() => setQrOpen(false)} fullWidth={true} maxWidth={"xl"}>
                <QrCodeScanner onScanSuccess={handleQrCodeScan}/>
            </Dialog>
        )
    }

    const renderBarCodeDialog = () => {
        return (
            <Dialog open={barCodeOpen} onClose={() => setBarCodeOpen(false)} fullWidth={true} maxWidth={"xl"}>
                <BarCodeScanner onScanSuccess={handleBarCodeScan}/>
            </Dialog>
        )
    }

    return (
        <React.Fragment>
            {renderSearchDialog()}
            {renderAddDialog()}
            {renderQrCodeDialog()}
            {renderBarCodeDialog()}
            <Grid container spacing={1}>
                <Grid item>
                    {renderAddButton()}
                    {renderSearchButton()}
                </Grid>
                <Grid item xs>
                    <Grid container spacing={1}>
                        <Grid item xs={6} md={3}>
                            <CustomTextField fullWidth={fullWidth} autoFocus={autoFocus}
                                             required={required}
                                             label={label} value={getValueKey()} disabled={disabled}
                                             onChange={onChangeValue}/>
                        </Grid>
                        <Grid item xs={6} md={9}>
                            <CustomTextField fullWidth={fullWidth} label={labelDescription}
                                             disabled={disabled}
                                             value={getValueDescription()}/>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </React.Fragment>
    )

});

export default ForeignKey;